import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
// core
import {
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
} from '@material-ui/core';

import { VIEW_MODE, EDIT_MODE } from '../../../utils/const/taskModes';

import useStyles from '../styles';
import QuestionShell from '../QuestionShell';
import useQuestionForm from '../QuestionShell/useQuestionForm';

function Video({
  identifier, pages, info, handleDeleteQuestion,
  mode, handleDuplicateQuestion, questionOptions,
}) {
  const styles = useStyles();
  const { t } = useTranslation();

  const page = pages?.[identifier - 1];

  const [localMemoryUnit, setLocalMemoryUnit] = useState(
    info?.minMemory ? info.minMemory.split(' ')[1] : page?.memoryUnit || 'KB',
  );
  const [localMemoryQuantity, setLocalMemoryQuantity] = useState(
    info?.minMemory ? info.minMemory.split(' ')[0] : page?.memoryQuantity || '0',
  );
  const [localVideoQuality, setLocalVideoQuality] = useState(
    info?.videoQuality || page?.videoQuality || 'medium',
  );

  const question = useQuestionForm({ identifier, pages, info });
  const {
    scheduleWrite, questionLogic, syncLocalField,
  } = question;

  // Los campos propios del video llegan por `name` en vez de `id`, y la memoria
  // mínima se guarda además como un solo string (`minMemory`).
  const handleOnChange = useCallback((event) => {
    const {
      id, checked, name, value, imageUrl,
    } = event.target;

    syncLocalField(id, value);
    if (name === 'memoryUnit') setLocalMemoryUnit(value);
    if (id === 'memoryQuantity') setLocalMemoryQuantity(value);
    if (name === 'videoQuality') setLocalVideoQuality(value);

    let key = id;
    let fixedValue = value;
    const updates = {};

    if (name === 'memoryUnit') {
      key = name;
      updates.minMemory = `${pages?.[identifier - 1]?.memoryQuantity || '0'} ${value}`;
    } else if (id === 'memoryQuantity') {
      updates.minMemory = `${value} ${pages?.[identifier - 1]?.memoryUnit || 'KB'}`;
      updates.memoryQuantity = value;
    } else if (name === 'videoQuality') {
      key = name;
    } else if (name === 'requiresTeleaudit') {
      key = name;
      fixedValue = checked;
    } else if (name === 'surveyTeleauditId') {
      key = name;
      fixedValue = value || null;
    }

    scheduleWrite({
      [key]: fixedValue,
      ...updates,
      questionChoices: null,
      questionLogic,
      imageUrl,
    });
  }, [identifier, pages, questionLogic, scheduleWrite, syncLocalField]);

  const requiresTeleaudit = info ? info.requiresTeleaudit : page.requiresTeleaudit;

  return (
    <QuestionShell
      {...question}
      handleOnChange={handleOnChange}
      identifier={identifier}
      info={info}
      mode={mode}
      titleKey="task.taskEdition.videoText.title"
      descriptionKey="task.taskEdition.videoText.description"
      handleDeleteQuestion={handleDeleteQuestion}
      handleDuplicateQuestion={handleDuplicateQuestion}
    >
      <div className={styles.memoryContainer}>
        {/* // TODO: Revisar porque no se está guardando */}
        <TextField
          id="memoryQuantity"
          type="number"
          placeholder={t('task.taskEdition.videoText.minMemory')}
          className={styles.memoryInput}
          InputProps={{ disableUnderline: true }}
          value={localMemoryQuantity}
          onChange={mode === EDIT_MODE ? handleOnChange : null}
          disabled={mode === VIEW_MODE}
        />
        <Select
          name="memoryUnit"
          value={localMemoryUnit}
          onChange={mode === EDIT_MODE ? handleOnChange : null}
          disabled={mode === VIEW_MODE}
          className={styles.memoryUnit}
          disableUnderline
        >
          <MenuItem value="KB">KB</MenuItem>
          <MenuItem value="MB">MB</MenuItem>
          <MenuItem value="GB">GB</MenuItem>
        </Select>
      </div>
      <div className={styles.qualityContainer}>
        <Select
          name="videoQuality"
          value={localVideoQuality}
          onChange={mode === EDIT_MODE ? handleOnChange : null}
          disabled={mode === VIEW_MODE}
          className={styles.memoryUnit}
          disableUnderline
          displayEmpty
        >
          <MenuItem value="" disabled hidden className={styles.placeholder}>
            {t('task.taskEdition.videoText.videoQuality')}
          </MenuItem>
          <MenuItem value="low">{t('task.taskEdition.videoText.low')}</MenuItem>
          <MenuItem value="medium">{t('task.taskEdition.videoText.medium')}</MenuItem>
          <MenuItem value="high">{t('task.taskEdition.videoText.high')}</MenuItem>
          <MenuItem value="veryHigh">{t('task.taskEdition.videoText.veryHigh')}</MenuItem>
        </Select>
      </div>
      {/* Switch to mark this question as a teleaudit question */}
      <div className={styles.switch}>
        <FormControlLabel
          control={
            <Switch
              checked={requiresTeleaudit}
              name="requiresTeleaudit"
              color="primary"
              onChange={mode === EDIT_MODE ? handleOnChange : null}
              disabled={mode === VIEW_MODE}
              size="small"
            />
          }
          label={t('task.taskEdition.videoText.videoRequiresRevision')}
          labelPlacement="start"
        />
      </div>

      {/* Dropdown para seleccionar encuesta de teleaudit */}
      {requiresTeleaudit && (
        <FormControl className={styles.formControl} style={{ marginTop: '10px', width: '100%' }}>
          <InputLabel id="survey-teleaudit-label">{t('task.taskEdition.videoText.selectSurvey') || 'Seleccionar encuesta'}</InputLabel>
          <Select
            labelId="survey-teleaudit-label"
            name="surveyTeleauditId"
            value={info ? info.surveyTeleauditId : page.surveyTeleauditId || ''}
            onChange={mode === EDIT_MODE ? handleOnChange : null}
            disabled={mode === VIEW_MODE}
            fullWidth
          >
            <MenuItem value="">
              <em>Ninguno</em>
            </MenuItem>
            {(questionOptions || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </QuestionShell>
  );
}

export default Video;
